import { Fragment, useEffect, useState } from 'react'
import { Icon as LegacyIcon } from '@ant-design/compatible'
import { Pagination } from 'antd'
import Timeline from '../Timeline'

import {
  ModuleHistoryQuery_node_Module_lock_state_history_LockEventConnection_nodes_LockEvent as LockEvent,
  useModuleHistoryQuery,
} from '~/gql/generated/graphql'
import { capitalize, formatDatetime, getIcon } from '~/utils'
import { EmptyComponent, LoadingComponent } from '~/components/Query/utils'
import { LockConfiguration } from '~/__generated__/globalTypes'
import { LockIcon } from './LockIcons'

interface LockStateHistory {
  id: string
  lockConfig?: LockConfiguration
}

/**
 * Lock state history.
 * eg viewable on  /scheme/8/vehicle/by_module_id/{module_id}
 * not to be confused with journey history
 * @param id
 */
export default ({ id, lockConfig }: LockStateHistory) => {
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const onPaginationChange = (clickedPage: number, pageSize: number) => {
    setPage(clickedPage)
    setPerPage(pageSize)
  }

  const { data, loading } = useModuleHistoryQuery({
    variables: {
      id,
      paginate: { page, per_page: perPage },
    },
  })

  let hasDualLocks = false
  if (lockConfig !== undefined) {
    hasDualLocks = [
      LockConfiguration.front_and_rear_lock,
      LockConfiguration.front_and_electronic_rear_lock,
    ].includes(lockConfig)
  }

  useEffect(() => {
    const numberOfItems = hasDualLocks ? 5 : 10
    setPerPage(numberOfItems)
  }, [hasDualLocks])

  if (loading) {
    return <LoadingComponent />
  }

  const { nodes, pagination } = data?.node?.lock_state_history || {}

  if (!nodes || nodes.length === 0) {
    return <EmptyComponent />
  }

  const itemWrapper = (item: LockEvent, i) => {
    return (
      <div className="mb-1" key={i}>
        {item.vehicle_lock_state ? (
          <LockIcon variant={item.vehicle_lock_state} />
        ) : (
          <LegacyIcon
            style={{ fontSize: '16px', paddingRight: 1 }}
            type={getIcon(item.overall_state?.toString() ?? 'unknown').icon}
          />
        )}
        <span style={{ fontWeight: 600 }}>
          {capitalize(item.vehicle_lock_state ?? item.overall_state)}
        </span>{' '}
        - {formatDatetime(item.time, 'time')}
        <ul style={{ fontSize: '12px' }} key={`ul${i}`}>
          {hasDualLocks && (
            <li key={`li1${i}`} style={{ fontWeight: 600 }}>
              {`Front lock: ${capitalize(item.front_lock_state)}`}
            </li>
          )}
          <li key={`li2${i}`} style={{ fontWeight: 600 }}>
            {`Rear lock: ${capitalize(item.rear_lock_state)}`}
          </li>
          {item.source && (
            <li key={`li3${i}`} style={{ fontWeight: 400 }}>
              {`Source: ${capitalize(item.source)}`}
            </li>
          )}
        </ul>
      </div>
    )
  }

  return (
    <Fragment>
      <Timeline items={nodes}>{itemWrapper}</Timeline>
      <Pagination
        hideOnSinglePage
        showSizeChanger
        pageSizeOptions={hasDualLocks ? ['10', '30', '50'] : ['20', '40', '60']}
        current={pagination?.current}
        total={pagination?.total}
        pageSize={perPage}
        onChange={onPaginationChange}
        onShowSizeChange={onPaginationChange}
        style={{ float: 'right' }}
      />
    </Fragment>
  )
}
